import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';

import { useGetCompanyInformation } from '@/api/directory';
import { PAGE_PARAM } from '@/constants/ObjectivePeriodLens';
import { setOrDelete, useReplaceSearch } from '@/hooks/utils/useReplaceSearch';
import { getCompanyAttributeList } from '@/utils/company';
import { keepOfferedValues, selectedIdsKey } from '@/utils/companyAttributeFilters';
import {
    hasActiveFilterIn,
    readAttributeFilters,
    readList,
    readStatuses,
    SEARCH_PARAM,
    STATUS_PARAM,
    writeAttributeFilters,
    writeList,
} from '@/utils/objectiveListAddress';

interface AddressedListFiltersOptions {
    /** What else an actual change of filter writes, in the same writing: the pillar scope. */
    withEachChange?: (next: URLSearchParams) => void;
}

/**
 * The filters of an objectives list -- name searched, states, attributes -- kept in the address so
 * they survive a step back, a reload and a shared link. A change replaces the current entry and,
 * like a change of period, drops the page: the list is numbered anew.
 */
export const useAddressedListFilters = ({ withEachChange }: AddressedListFiltersOptions = {}) => {
    const location = useLocation();
    const replaceParams = useReplaceSearch();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const searchText = params.get(SEARCH_PARAM) ?? '';
    const selectedStatuses = useMemo(() => readStatuses(params), [params]);
    const { data: company } = useGetCompanyInformation();
    const addressedFilters = useMemo(() => readAttributeFilters(params), [params]);
    // Until the company says which values it still offers, the address is taken at its word.
    const attributeFilters = useMemo(
        () =>
            company && addressedFilters
                ? keepOfferedValues(addressedFilters, getCompanyAttributeList(company))
                : addressedFilters,
        [company, addressedFilters],
    );
    const hasActiveFilter = hasActiveFilterIn(params);

    const replaceFilters = useCallback(
        (change: (next: URLSearchParams) => void) =>
            replaceParams((next) => {
                const before = next.toString();
                change(next);

                if (next.toString() === before) {
                    return;
                }

                next.delete(PAGE_PARAM);
                withEachChange?.(next);
            }),
        [replaceParams, withEachChange],
    );

    // A state the list does not know, or a value the company no longer offers, leaves the address
    // too, in one writing, or a link would bring back a filter nobody can see.
    const dropsStatus = selectedStatuses.length !== readList(params, STATUS_PARAM).length;
    const dropsAttribute =
        !!attributeFilters && selectedIdsKey(attributeFilters) !== selectedIdsKey(addressedFilters);
    useEffect(() => {
        if (!dropsStatus && !dropsAttribute) {
            return;
        }

        replaceParams((next) => {
            writeList(next, STATUS_PARAM, selectedStatuses);

            if (attributeFilters) {
                writeAttributeFilters(next, attributeFilters);
            }
        });
    }, [dropsStatus, dropsAttribute, selectedStatuses, attributeFilters, replaceParams]);

    const setSearchText = useCallback(
        (text: string) => replaceFilters((next) => setOrDelete(next, SEARCH_PARAM, text)),
        [replaceFilters],
    );

    const setSelectedStatuses = useCallback(
        (statuses: string[]) => replaceFilters((next) => writeList(next, STATUS_PARAM, statuses)),
        [replaceFilters],
    );

    const setAttributeFilters = useCallback(
        (filters: SelectedFilters) =>
            replaceFilters((next) => writeAttributeFilters(next, filters)),
        [replaceFilters],
    );

    return {
        searchText,
        setSearchText,
        selectedStatuses,
        setSelectedStatuses,
        attributeFilters,
        setAttributeFilters,
        hasActiveFilter,
    };
};
